<template>
    <Head :title="`Edit: ${policy.name}`" />

    <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <!-- Back Button -->
        <div class="mb-4">
            <Link
                :href="route('settings.incidents.sla-policies.index')"
                class="inline-flex items-center text-sm text-gray-600 dark:text-secondary-400 hover:text-gray-900 dark:hover:text-white"
            >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to SLA Policies
            </Link>
        </div>

        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-secondary-700">
                <div class="flex items-center gap-3">
                    <div class="flex-1">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit SLA Policy</h2>
                        <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Update response and investigation time requirements for incidents matching specific criteria.</p>
                    </div>
                    <InfoButton title="Editing SLA Policies" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Edit SLA Policy page allows you to modify an existing Service Level Agreement that
                            defines time-based performance commitments for incident management. You can adjust response
                            timeframes (when triage must occur), investigation timeframes (when full investigations must
                            complete), update matching criteria (incident types, near miss filter), change policy priority,
                            and toggle active status. Changes to SLA policies apply to future incidents only and do not
                            retroactively affect existing incidents that were already matched to this policy.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Policy Name:</strong> Update the descriptive name to clearly identify the purpose
                              (e.g., "Critical Safety - 2hr Response")<br>
                            • <strong>Description:</strong> Revise the explanation of what this policy covers and why
                              these timeframes were established<br>
                            • <strong>Incident Type:</strong> Change which incident type this policy applies to, or
                              select "All Types" to apply broadly<br>
                            • <strong>Incident/Near Miss Filter:</strong> Adjust whether this applies to both incidents
                              and near misses, incidents only, or near misses only<br>
                            • <strong>Response Time Hours:</strong> Modify hours from incident report to triage
                              completion (when manager must assess and assign)<br>
                            • <strong>Investigation Time Hours:</strong> Adjust hours from investigation start to
                              completion (only applies if triage selects investigation path)<br>
                            • <strong>Active Status:</strong> Check to activate or uncheck to deactivate - inactive
                              policies don't apply to new incidents<br>
                            • Click <strong>Update SLA Policy</strong> to save changes, or <strong>Cancel</strong>
                              to discard<br>
                            • Note: This policy automatically applies to all severities, priorities, and locations
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Changes apply to future incidents only</strong> - Existing incidents that were
                              already matched to this policy retain their original SLA timeframes<br>
                            • <strong>Consider existing workload</strong> - Shortening SLA times may create unrealistic
                              expectations if incidents are currently being handled at longer timeframes<br>
                            • <strong>Communicate changes to teams</strong> - If you significantly modify response or
                              investigation times, inform managers and responders who will be held to new timeframes<br>
                            • <strong>Deactivate instead of delete</strong> - Keep historical SLA policies inactive
                              rather than deleting to preserve audit trail and data integrity<br>
                            • <strong>Test with inactive first</strong> - If making major changes, consider creating a
                              new policy as inactive, reviewing it, then activating when ready<br>
                            • <strong>Monitor breach rates</strong> - If an SLA policy has high breach rates, consider
                              adjusting timeframes to be more realistic<br>
                            • <strong>Review matching criteria carefully</strong> - Changing incident type or near miss
                              filter affects which incidents this policy applies to going forward<br>
                            • <strong>Document rationale for changes</strong> - Update description field to explain why
                              timeframes were adjusted<br>
                            • <strong>Consider business hours</strong> - Remember that calendar hours continue during
                              nights and weekends unless your system accounts for business hours<br>
                            • <strong>Response time is mandatory</strong> - Every matching incident must be triaged
                              within response time hours<br>
                            • <strong>Investigation time is conditional</strong> - Only applies if triage decision
                              selects "Investigation" path rather than "No Action" or "Quick Fix"<br>
                            • Changes take effect immediately for new incidents matching this policy - incidents already
                              in progress keep their original SLA deadlines
                        </p>
                    </InfoButton>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                <!-- Basic Information -->
                <div class="space-y-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>

                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                            Policy Name <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.name }"
                            placeholder="e.g., Critical Safety Incidents - 2 Hour Response"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                    </div>

                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            placeholder="Describe when this SLA policy applies..."
                        ></textarea>
                    </div>
                </div>

                <!-- Matching Criteria -->
                <div class="space-y-6 pt-6 border-t border-gray-200 dark:border-secondary-700">
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Matching Criteria</h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Select incident type and whether this applies to incidents, near misses, or both. This policy will automatically apply to all severities, priorities, and locations.</p>
                    </div>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="incident_type_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Incident Type
                            </label>
                            <select
                                id="incident_type_id"
                                v-model="form.incident_type_id"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">All Types</option>
                                <option v-for="type in incidentTypes" :key="type.id" :value="type.id">
                                    {{ type.name }}
                                </option>
                            </select>
                        </div>

                        <div>
                            <label for="near_miss_only" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Incident/Near Miss Filter
                            </label>
                            <select
                                id="near_miss_only"
                                v-model="form.near_miss_only"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option :value="null">Both Incidents and Near Misses</option>
                                <option :value="false">Incidents Only</option>
                                <option :value="true">Near Misses Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- SLA Times -->
                <div class="space-y-6 pt-6 border-t border-gray-200 dark:border-secondary-700">
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">SLA Times</h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Define time targets in hours. Leave empty if not applicable.</p>
                    </div>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="response_time_hours" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Response Time (hours)
                            </label>
                            <input
                                id="response_time_hours"
                                v-model.number="form.response_time_hours"
                                type="number"
                                min="0"
                                step="0.5"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="e.g., 2"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Time to complete triage/initial response</p>
                        </div>

                        <div>
                            <label for="investigation_time_hours" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Investigation Time (hours)
                            </label>
                            <input
                                id="investigation_time_hours"
                                v-model.number="form.investigation_time_hours"
                                type="number"
                                min="0"
                                step="0.5"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="e.g., 48"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Time to complete full investigation</p>
                        </div>
                    </div>
                </div>

                <!-- Status -->
                <div class="space-y-4 pt-6 border-t border-gray-200 dark:border-secondary-700">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Status</h3>

                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-secondary-600 rounded"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700 dark:text-secondary-300">Active</label>
                            <p class="text-gray-500 dark:text-secondary-400">Enable this SLA policy to apply to matching incidents</p>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-secondary-700">
                    <Link
                        :href="route('settings.incidents.sla-policies.index')"
                        class="px-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {{ form.processing ? 'Updating...' : 'Update SLA Policy' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policy: {
        type: Object,
        required: true,
    },
    incidentTypes: {
        type: Array,
        required: true,
    },
});

// Initialize form with useSecureForm for company context protection
const form = useSecureForm({
    name: props.policy.name,
    description: props.policy.description || '',
    incident_type_id: props.policy.incident_type_id || '',
    near_miss_only: props.policy.near_miss_only,
    response_time_hours: props.policy.response_time_hours,
    investigation_time_hours: props.policy.investigation_time_hours,
    is_active: props.policy.is_active,
});

const submit = () => {
    form.put(route('settings.incidents.sla-policies.update', props.policy.id), {
        preserveScroll: true,
    });
};
</script>
